import React, { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import RouteProtegee from './components/RouteProtegee';
import OfflineBanner from './components/OfflineBanner';
import DevUserSwitcher from './components/DevUserSwitcher';
import Accueil from './pages/Accueil';
import Inscription from './pages/Inscription';
import Connexion from './pages/Connexion';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import TerresDisponibles from './pages/TerresDisponibles';
import EnregistrerTerre from './pages/EnregistrerTerre';
import LandDetail from './pages/LandDetail';
import CreateAuction from './pages/CreateAuction';
import AuctionDetail from './pages/AuctionDetail';
import MyProfile from './pages/MyProfile';
import MyReservations from './pages/MyReservations';
import MyReviews from './pages/MyReviews';
import CommunityHarvests from './pages/CommunityHarvests';
import Security from './pages/Security';
import Paiement from './pages/Paiement';
import PaiementSucces from './pages/PaiementSucces';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import Guides from './pages/Guides';
import Faune from './pages/Faune';
import Marche from './pages/Marche';
import Communaute from './pages/Communaute';
import NotFound from './pages/NotFound';
import MyCredits from './pages/MyCredits';
import SubmitObservation from './pages/SubmitObservation';
import MyHunts from './pages/MyHunts';
import PersonalMap from './pages/PersonalMap';
import SocialFeed from './pages/SocialFeed';
import RegionStats from './pages/RegionStats';
import CommunityMap from './pages/CommunityMap';
import MyReports from './pages/MyReports';
import SubmitReport from './pages/SubmitReport';
import SubmitDamageReport from './pages/SubmitDamageReport';
import MyDamageReports from './pages/MyDamageReports';
import HuntersMatches from './pages/HuntersMatches';
import MySubscription from './pages/MySubscription';
import Recettes from './pages/Recettes';
import DetailRecette from './pages/DetailRecette';
import SoumettreRecette from './pages/SoumettreRecette';
import './styles/design-system.css';

export const LangContext = React.createContext({ lang: 'fr', setLang: () => {} });

// Read the persisted language choice (defaults to 'fr'). Validates the
// stored value so a corrupted localStorage can't crash render.
function lireLangueInitiale() {
  try {
    const stored = localStorage.getItem('lang');
    return stored === 'en' || stored === 'fr' ? stored : 'fr';
  } catch {
    return 'fr';
  }
}

export default function App() {
  const [lang, setLangState] = useState(lireLangueInitiale);

  // Persist on every change so the user's language preference survives
  // refreshes, new tabs, and dev-switcher reloads.
  const setLang = React.useCallback((nouvelle) => {
    setLangState(nouvelle);
    try {
      localStorage.setItem('lang', nouvelle);
    } catch {
      /* localStorage full or disabled — ignore */
    }
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <AuthProvider>
        <BrowserRouter>
          <OfflineBanner />
          <Routes>
            <Route path="/" element={<Accueil />} />
            <Route path="/signup" element={<Inscription />} />
            <Route path="/login" element={<Connexion />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <RouteProtegee>
                  <Dashboard />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-profile"
              element={
                <RouteProtegee>
                  <MyProfile />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-lands"
              element={
                <RouteProtegee>
                  <Dashboard />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-reservations"
              element={
                <RouteProtegee>
                  <MyReservations />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-reviews"
              element={
                <RouteProtegee>
                  <MyReviews />
                </RouteProtegee>
              }
            />
            <Route
              path="/register-land"
              element={
                <RouteProtegee>
                  <EnregistrerTerre />
                </RouteProtegee>
              }
            />
            <Route
              path="/lands/:id/edit"
              element={
                <RouteProtegee>
                  <EnregistrerTerre />
                </RouteProtegee>
              }
            />
            <Route
              path="/create-auction"
              element={
                <RouteProtegee>
                  <CreateAuction />
                </RouteProtegee>
              }
            />
            <Route path="/auctions/:id" element={<AuctionDetail />} />
            <Route path="/lands" element={<TerresDisponibles />} />
            <Route path="/lands/:id" element={<LandDetail />} />
            <Route
              path="/security"
              element={
                <RouteProtegee>
                  <Security />
                </RouteProtegee>
              }
            />
            <Route
              path="/payment/success"
              element={
                <RouteProtegee>
                  <PaiementSucces />
                </RouteProtegee>
              }
            />
            <Route
              path="/payment/:miseId"
              element={
                <RouteProtegee>
                  <Paiement />
                </RouteProtegee>
              }
            />
            <Route path="/privacy-policy" element={<PolitiqueConfidentialite />} />

            {/* Credits and observations */}
            <Route
              path="/my-credits"
              element={
                <RouteProtegee>
                  <MyCredits />
                </RouteProtegee>
              }
            />
            <Route
              path="/submit-observation"
              element={
                <RouteProtegee>
                  <SubmitObservation />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-hunts"
              element={
                <RouteProtegee>
                  <MyHunts />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-map"
              element={
                <RouteProtegee>
                  <PersonalMap />
                </RouteProtegee>
              }
            />
            <Route
              path="/social"
              element={
                <RouteProtegee>
                  <SocialFeed />
                </RouteProtegee>
              }
            />
            <Route path="/stats" element={<RegionStats />} />
            <Route
              path="/my-subscription"
              element={
                <RouteProtegee>
                  <MySubscription />
                </RouteProtegee>
              }
            />
            <Route path="/community-map" element={<CommunityMap />} />
            <Route
              path="/my-reports"
              element={
                <RouteProtegee>
                  <MyReports />
                </RouteProtegee>
              }
            />
            <Route
              path="/submit-report"
              element={
                <RouteProtegee>
                  <SubmitReport />
                </RouteProtegee>
              }
            />
            {/* Module A — Depredation (farmer = submit damage, hunter = matches) */}
            <Route
              path="/submit-damage-report"
              element={
                <RouteProtegee>
                  <SubmitDamageReport />
                </RouteProtegee>
              }
            />
            <Route
              path="/my-damage-reports"
              element={
                <RouteProtegee>
                  <MyDamageReports />
                </RouteProtegee>
              }
            />
            <Route
              path="/hunters-matches"
              element={
                <RouteProtegee>
                  <HuntersMatches />
                </RouteProtegee>
              }
            />

            {/* Recipes */}
            <Route path="/recipes" element={<Recettes />} />
            <Route path="/recipes/:id" element={<DetailRecette />} />
            <Route
              path="/recipes/:id/edit"
              element={
                <RouteProtegee>
                  <SoumettreRecette />
                </RouteProtegee>
              }
            />
            <Route
              path="/submit-recipe"
              element={
                <RouteProtegee>
                  <SoumettreRecette />
                </RouteProtegee>
              }
            />

            {/* Public "Coming Soon" pages */}
            <Route path="/guides" element={<Guides />} />
            <Route path="/wildlife" element={<Faune />} />
            <Route path="/market" element={<Marche />} />
            <Route path="/community" element={<Communaute />} />
            <Route path="/community-harvests" element={<CommunityHarvests />} />

            {/* Route catch-all 404 — DOIT rester en dernière position */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <DevUserSwitcher />
        </BrowserRouter>
      </AuthProvider>
    </LangContext.Provider>
  );
}
